import { differenceInSeconds, formatDuration, intervalToDuration, parseISO } from "date-fns";
import { useCallback, useEffect, useState } from "react";
import { FabrixIcon, formatDate, translate } from "../../common";
import { useIcon } from "../../hooks";
import useStateRef from "react-usestateref";


export interface ActivityClockProps {
    time?: Date | string;
    className?: string;
    icon?: string;
    style?: React.CSSProperties;
}

/**
 * Renders a relative time display with an optional icon for activity timestamps.
 * @param props Activity clock configuration including the timestamp, CSS overrides, and icon choice.
 * @returns JSX element showing the elapsed time since the referenced timestamp.
 */
export default function ActivityClock(props: ActivityClockProps) {

    const icon = useIcon();
    const [_elapsedTime, setElapsedTime] = useState<string>('');
    const [_time, setTime, _timeRef] = useStateRef<Date | string | undefined>(props.time)

    /**
     * Produces a localized duration string or formatted date depending on elapsed time.
     * @param date Source timestamp as a Date or ISO string.
     * @returns Human-readable elapsed duration or formatted date when older than one day.
     */
    const showDuration = (date?: Date | string) => {
        if (date) {
            if (typeof date === 'string') {
                date = parseISO(date)
            }
            let endDate = new Date();
            let timeDiff = endDate.getTime() - date.getTime();
            let suffix = timeDiff > 10 * 1000 ? translate("AGO") : '';
            let duration = intervalToDuration({ start: date, end: endDate })
            if (!duration.days || duration.days < 1)
                return `${formatDuration(duration)} ${suffix}`
            else
                return formatDate(date);

        }
        return '';
    }

    useEffect(() => {
        setTime(props.time)
    }, [props.time])

    useEffect(() => {
        updateTimeLeft();
    }, [_timeRef.current])

    /**
     * Updates the elapsed time display and schedules the next refresh.
     */
    const updateTimeLeft = useCallback(async () => {
        if (_time) {
            let duration = showDuration(_timeRef.current)
            setElapsedTime(duration);
            window.setTimeout(updateTimeLeft, 10 * 1000);
        }
    }, [_time]);

    return (
        <div style={props.style} className={props.className}>
            <span style={{ marginRight: 6 }} className={icon(props.icon ?? FabrixIcon.calendar)}></span>
            <span >{_elapsedTime}</span>

        </div>)

}